import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Rocket,
  AlertCircle,
} from 'lucide-react'
import {
  getCredentialsStatus,
  storeAppleCredentials,
  getMobilePublishStatus,
  configureMobilePublish,
  triggerMobilePublish,
  retryMobileSubmit,
  type MobilePublishStatus,
  type MobileDeployment,
} from '@/lib/mobile-api'

interface PublishModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  projectSlug: string
}

type Step = 'loading' | 'credentials' | 'configure' | 'deploy'

export function PublishModal({ open, onOpenChange, projectId, projectSlug }: PublishModalProps) {
  const { t } = useTranslation('mobile')
  const [step, setStep] = useState<Step>('loading')
  const [appleConfigured, setAppleConfigured] = useState(false)
  const [publishStatus, setPublishStatus] = useState<MobilePublishStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Credential form
  const [credForm, setCredForm] = useState({
    asc_key_id: '',
    asc_issuer_id: '',
    asc_key_content: '',
    apple_team_id: '',
    apple_team_type: 'INDIVIDUAL' as string,
    company_name: '',
  })

  // Config form
  const [bundleId, setBundleId] = useState('')

  const publishStatusRef = useRef(publishStatus)
  publishStatusRef.current = publishStatus

  const refresh = useCallback(async () => {
    let credOk = false
    try {
      const credStatus = await getCredentialsStatus()
      setAppleConfigured(credStatus.apple_configured)
      credOk = credStatus.apple_configured
    } catch {
      setStep('credentials')
      return
    }

    try {
      const status = await getMobilePublishStatus(projectId)
      setPublishStatus(status)
      setBundleId(status.config.ios_bundle_id || `com.${projectSlug.replace(/-/g, '').toLowerCase()}`)

      if (!credOk) {
        setStep('credentials')
      } else if (!status.config.ios_bundle_id) {
        setStep('configure')
      } else {
        setStep('deploy')
      }
    } catch {
      setStep(credOk ? 'configure' : 'credentials')
    }
  }, [projectId, projectSlug])

  useEffect(() => {
    if (open) {
      setStep('loading')
      setError(null)
      refresh()
    }
  }, [open, refresh])

  // Poll when deploying (use ref to avoid dependency on publishStatus)
  useEffect(() => {
    if (!open || step !== 'deploy') return

    const interval = setInterval(async () => {
      const current = publishStatusRef.current
      if (!current?.ios) return
      const isActive = ['pending', 'building', 'submitting'].includes(current.ios.status)
      if (!isActive) return

      try {
        const s = await getMobilePublishStatus(projectId)
        setPublishStatus(s)
      } catch {
        // Ignore poll errors
      }
    }, 5000)
    return () => clearInterval(interval)
  }, [open, step, projectId])

  const handleSaveCredentials = async () => {
    setError(null)
    setSaving(true)
    try {
      await storeAppleCredentials(credForm)
      setAppleConfigured(true)
      setStep('configure')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveConfig = async () => {
    setError(null)
    setSaving(true)
    try {
      await configureMobilePublish(projectId, { ios_bundle_id: bundleId })
      await refresh()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDeploy = async () => {
    setError(null)
    setSaving(true)
    try {
      await triggerMobilePublish(projectId, ['ios'])
      const s = await getMobilePublishStatus(projectId)
      setPublishStatus(s)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const isValidBundleId = /^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*)+$/.test(bundleId)
  const isValidKeyId = /^[A-Z0-9]{10}$/.test(credForm.asc_key_id)
  const isValidIssuerId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(credForm.asc_issuer_id)
  const isValidP8 = credForm.asc_key_content.includes('-----BEGIN PRIVATE KEY-----') && credForm.asc_key_content.includes('-----END PRIVATE KEY-----')
  const isValidTeamId = /^[A-Z0-9]{10}$/.test(credForm.apple_team_id)
  const credFormValid = isValidKeyId && isValidIssuerId && isValidP8 && isValidTeamId && !!credForm.apple_team_type

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            {t('publish.title')}
          </DialogTitle>
          <DialogDescription>
            {t('publish.description')}
          </DialogDescription>
        </DialogHeader>

        {step === 'loading' && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {step === 'credentials' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">1</div>
              {t('credentials.connectApple')}
            </div>

            <div className="rounded-lg bg-muted p-3 text-sm space-y-1">
              <p className="font-medium">{t('credentials.apiKeyInstructions.title')}</p>
              <ol className="list-decimal list-inside space-y-0.5 text-muted-foreground text-xs">
                <li><a href="https://appstoreconnect.apple.com/access/integrations/api" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t('credentials.apiKeyInstructions.step1')}</a></li>
                <li>{t('credentials.apiKeyInstructions.step2')}</li>
                <li>{t('credentials.apiKeyInstructions.step3')}</li>
                <li><a href="https://developer.apple.com/account" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t('credentials.apiKeyInstructions.step4')}</a></li>
              </ol>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="modal_key_id" className="text-xs">{t('credentials.fields.keyId')}</Label>
                <Input id="modal_key_id" placeholder="XXXXXXXXXX" value={credForm.asc_key_id} onChange={(e) => setCredForm({ ...credForm, asc_key_id: e.target.value.toUpperCase() })} />
                {credForm.asc_key_id && !isValidKeyId && <p className="text-xs text-destructive">{t('credentials.validation.keyIdFormat')}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="modal_issuer_id" className="text-xs">{t('credentials.fields.issuerId')}</Label>
                <Input id="modal_issuer_id" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={credForm.asc_issuer_id} onChange={(e) => setCredForm({ ...credForm, asc_issuer_id: e.target.value })} />
                {credForm.asc_issuer_id && !isValidIssuerId && <p className="text-xs text-destructive">{t('credentials.validation.issuerIdFormat')}</p>}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="modal_team_id" className="text-xs">{t('credentials.fields.teamId')}</Label>
                <Input id="modal_team_id" placeholder="ABCD1234EF" value={credForm.apple_team_id} onChange={(e) => setCredForm({ ...credForm, apple_team_id: e.target.value.toUpperCase() })} />
                {credForm.apple_team_id && !isValidTeamId && <p className="text-xs text-destructive">{t('credentials.validation.teamIdFormat')}</p>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('credentials.fields.teamType')}</Label>
                <Select value={credForm.apple_team_type} onValueChange={(value) => setCredForm({ ...credForm, apple_team_type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INDIVIDUAL">{t('credentials.fields.teamTypeIndividual')}</SelectItem>
                    <SelectItem value="COMPANY_OR_ORGANIZATION">{t('credentials.fields.teamTypeCompany')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="modal_company" className="text-xs">{t('credentials.fields.companyName')}</Label>
              <Input id="modal_company" placeholder="Your Company Inc." value={credForm.company_name} onChange={(e) => setCredForm({ ...credForm, company_name: e.target.value })} />
            </div>

            <div className="space-y-1">
              <Label htmlFor="modal_key_content" className="text-xs">{t('credentials.fields.apiKeyContent')}</Label>
              <Textarea id="modal_key_content" placeholder={t('credentials.fields.apiKeyPlaceholder')} rows={3} className="font-mono text-xs" value={credForm.asc_key_content} onChange={(e) => setCredForm({ ...credForm, asc_key_content: e.target.value })} />
              {credForm.asc_key_content && !isValidP8 && <p className="text-xs text-destructive">{t('credentials.validation.apiKeyFormat')}</p>}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button className="w-full" onClick={handleSaveCredentials} disabled={saving || !credFormValid}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('credentials.saveCredentials')}
            </Button>
          </div>
        )}

        {step === 'configure' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="default" className="bg-green-600 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />{t('credentials.appleConnected')}</Badge>
            </div>

            <div className="flex items-center gap-2 text-sm font-medium">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">2</div>
              {t('configure.setBundleId')}
            </div>

            <div className="space-y-1">
              <Label htmlFor="modal_bundle_id" className="text-xs">{t('configure.bundleIdLabel')}</Label>
              <Input
                id="modal_bundle_id"
                placeholder="com.company.appname"
                value={bundleId}
                onChange={(e) => setBundleId(e.target.value.toLowerCase())}
              />
              {bundleId && !isValidBundleId && (
                <p className="text-xs text-destructive">{t('credentials.validation.bundleIdFormat')}</p>
              )}
              <p className="text-xs text-muted-foreground">{t('configure.bundleIdHint')}</p>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button className="w-full" onClick={handleSaveConfig} disabled={saving || !isValidBundleId}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('configure.continue')}
            </Button>
          </div>
        )}

        {step === 'deploy' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="default" className="bg-green-600 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />{t('credentials.appleConnected')}</Badge>
              <Badge variant="outline" className="text-xs">{publishStatus?.config.ios_bundle_id}</Badge>
            </div>

            {publishStatus?.ios ? (
              <DeploymentStatus deployment={publishStatus.ios} projectId={projectId} onStatusChange={refresh} />
            ) : (
              <div className="rounded-lg border border-dashed p-6 text-center">
                <Rocket className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">{t('publish.readyToDeploy')}</p>
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            {(!publishStatus?.ios || publishStatus.ios.status === 'live' || (publishStatus.ios.status === 'failed' && !publishStatus.ios.error_message?.includes('APP_CREATION_FAILED'))) && (
              <Button className="w-full" onClick={handleDeploy} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Rocket className="h-4 w-4 mr-2" />
                {publishStatus?.ios?.status === 'live' ? t('publish.deployNewVersion') : t('publish.deployButton')}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function DeploymentStatus({ deployment, projectId, onStatusChange }: {
  deployment: MobileDeployment
  projectId: number
  onStatusChange: () => void
}) {
  const { t } = useTranslation('mobile')
  const isActive = ['pending', 'building', 'submitting'].includes(deployment.status)
  const isAppCreationFailed = deployment.status === 'failed' && deployment.error_message?.includes('APP_CREATION_FAILED')
  const [ascAppId, setAscAppId] = useState('')
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)

  const handleRetrySubmit = async () => {
    setRetryError(null)
    setRetrying(true)
    try {
      await retryMobileSubmit(projectId, ascAppId)
      onStatusChange()
    } catch (e: any) {
      setRetryError(e.message)
    } finally {
      setRetrying(false)
    }
  }

  const statusText: Record<string, string> = {
    pending: t('publish.status.queued'),
    building: t('publish.status.building'),
    submitting: t('publish.status.submitting'),
    live: t('publish.status.live'),
    failed: t('publish.status.failed'),
  }

  return (
    <div className="rounded-lg border p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {deployment.status === 'live' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
          {deployment.status === 'failed' && <XCircle className="h-4 w-4 text-destructive" />}
          {isActive && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          <span className="text-sm font-medium">
            {statusText[deployment.status]}
          </span>
        </div>
        {deployment.duration != null && deployment.duration > 0 && deployment.status === 'live' && (
          <span className="text-xs text-muted-foreground">{Math.round(deployment.duration / 60)}m</span>
        )}
      </div>

      {isAppCreationFailed ? (
        <div className="space-y-3">
          <div className="rounded bg-amber-50 dark:bg-amber-950/30 p-3 text-sm space-y-2">
            <p className="font-medium text-amber-800 dark:text-amber-200">{t('publish.appCreationFailed.title')}</p>
            <ol className="list-decimal list-inside space-y-1 text-xs text-amber-700 dark:text-amber-300">
              <li><a href="https://appstoreconnect.apple.com/apps" target="_blank" rel="noopener noreferrer" className="underline">App Store Connect</a></li>
              <li>{t('publish.appCreationFailed.step2')}</li>
              <li>{t('publish.appCreationFailed.step3').replace('<0>', '').replace('</0>', '')}</li>
              <li>{t('publish.appCreationFailed.step4')}</li>
            </ol>
          </div>
          <div className="space-y-1">
            <Label htmlFor="asc_app_id" className="text-xs">{t('publish.ascAppId.label')}</Label>
            <Input
              id="asc_app_id"
              placeholder={t('publish.ascAppId.placeholder')}
              value={ascAppId}
              onChange={(e) => setAscAppId(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          {retryError && <p className="text-xs text-destructive">{retryError}</p>}
          <Button className="w-full" onClick={handleRetrySubmit} disabled={retrying || !ascAppId}>
            {retrying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('publish.retrySubmit')}
          </Button>
        </div>
      ) : deployment.status === 'failed' && deployment.error_message ? (
        <div className="flex items-start gap-2 rounded bg-destructive/10 p-2">
          <AlertCircle className="h-3 w-3 text-destructive mt-0.5 shrink-0" />
          <p className="text-xs text-destructive">{deployment.error_message}</p>
        </div>
      ) : null}

      {isActive && (
        <>
          <div className="w-full bg-muted rounded-full h-1.5">
            <div
              className="bg-primary h-1.5 rounded-full transition-all duration-500"
              style={{
                width: deployment.status === 'pending' ? '10%' : deployment.status === 'building' ? '50%' : '85%'
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground">{t('publish.closeHint')}</p>
        </>
      )}
    </div>
  )
}
